// src/components/AddToCart.js
import { useState, useEffect } from "react";
import { Button, TextField, Typography, Stack } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useData } from "../hooks/useData";
import { useEcommerce } from "../hooks/useEcommerce";

const AddToCart = () => {
  const {
    canvas,
    mivaGlobals,
    signCategoryState,
    signCategorySizeState,
    upsaleSignPrice,
  } = useData();
  const { addToBasket } = useEcommerce();
  const [isDisabled, setIsDisabled] = useState(false);
  const [quantity, setQuantity] = useState(1);

  // Calculate the total price by multiplying upsaleSignPrice by quantity
  const totalPrice = ((Number(upsaleSignPrice) || 0) * quantity).toFixed(2);

  useEffect(() => {
    if (canvas) {
      const updateDisabledState = () => {
        const disabled = canvas.getObjects().length === 0;
        setIsDisabled(disabled);
      };
      canvas.on("object:added", updateDisabledState);
      canvas.on("object:removed", updateDisabledState);
      updateDisabledState();
      return () => {
        canvas.off("object:added", updateDisabledState);
        canvas.off("object:removed", updateDisabledState);
      };
    }
  }, [canvas]);

  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (value >= 1) {
      setQuantity(value);
    } else {
      setQuantity(1);
    }
  };

  const handleClick = async () => {
    if (!mivaGlobals) {
      return;
    }

    try {
      await addToBasket(mivaGlobals, quantity);
    } catch (error) {
      // Errors are handled by useEcommerce
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {/* Quantity Input */}
      <TextField
        type="number"
        label="Quantity"
        value={quantity}
        onChange={handleQuantityChange}
        size="small"
        inputProps={{ min: 1 }}
        sx={{ width: "100px" }}
      />

      {/* Category Name */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: "Roboto",
          fontWeight: 500,
          color: "text.primary",
        }}
      >
        {signCategoryState?.name || "No Product"}
      </Typography>

      {/* Sizes */}
      <Typography
        variant="body1"
        sx={{
          fontFamily: "Roboto",
          fontWeight: 400,
          color: "text.secondary",
        }}
      >
        {signCategorySizeState?.width || "N/A"} x{" "}
        {signCategorySizeState?.height || "N/A"}
      </Typography>

      {/* Price */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: "Roboto",
          fontWeight: 700,
          color: "error.main", // #dd2c00
        }}
      >
        ${totalPrice}
      </Typography>

      {/* Add To Cart Button */}
      <Button
        variant="contained"
        color="success" // Changed from "primary" to #388e3c
        disabled={isDisabled}
        onClick={handleClick}
        startIcon={<ShoppingCartIcon sx={{ width: 20, height: 20 }} />}
        className="btn-gtm-ap"
        sx={{ textTransform: "none" }}
      >
        <Typography>Add To Cart</Typography>
      </Button>
    </Stack>
  );
};

export default AddToCart;
